import ExcelJS from "exceljs";
import {
  buildMedicalFortuneSheetData,
  downloadMedicalTablePdf,
  ensureExtension,
  triggerBlobDownload,
} from "./medicalSheetCommon";

export const VITAL_SIGNS_HEADERS = [
  "No.",
  "Customer",
  "DM",
  "Weight",
  "BP/HR/pulse O2/temp",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
  "Sun",
];

const COLUMN_COUNT = VITAL_SIGNS_HEADERS.length;
const BASE_COL_WIDTHS_PX = [48, 240, 56, 72, 150, 78, 78, 78, 78, 78, 78];

/** Display name as saved on the chart row (Last, First Middle). */
export function vitalSignsCustomerDisplayName(raw) {
  return `${raw ?? ""}`.replace(/\s+/g, " ").trim();
}

const EMPTY_VITAL_ROW = {
  customer_id: "",
  no: 0,
  name: "",
  diabetes: "",
  weight: "",
  bp: "",
  sugar: "",
  tue: "",
  wed: "",
  thu: "",
  fri: "",
  sat: "",
  sun: "",
};

const MIN_VITAL_DATA_ROWS = 30;

/** Keep named roster rows (active AMDC members) and pad blank rows after them. */
export function keepVitalSignsRosterRows(rows = []) {
  const assigned = [];
  rows.forEach((row) => {
    const hasPerson =
      `${row?.customer_id || ""}`.trim() || vitalSignsCustomerDisplayName(row?.name);
    if (!hasPerson) return;
    assigned.push({ ...row, name: vitalSignsCustomerDisplayName(row?.name) });
  });
  const dataRowCount = Math.max(MIN_VITAL_DATA_ROWS, assigned.length);
  const out = [...assigned];
  while (out.length < dataRowCount) out.push({ ...EMPTY_VITAL_ROW });
  return out.map((row, index) => ({ ...row, no: index + 1 }));
}

export function rowToVitalSignsCells(row) {
  return [
    `${row?.no ?? ""}`,
    vitalSignsCustomerDisplayName(row?.name),
    `${row?.diabetes ?? ""}`,
    `${row?.weight ?? ""}`,
    `${row?.bp ?? ""}`,
    `${row?.tue ?? ""}`,
    `${row?.wed ?? ""}`,
    `${row?.thu ?? ""}`,
    `${row?.fri ?? ""}`,
    `${row?.sat ?? ""}`,
    `${row?.sun ?? ""}`,
  ];
}

export function buildVitalSignsSheetModel(payload) {
  // Always use canonical headers so legacy day files that still have Mon are ignored.
  const headers = VITAL_SIGNS_HEADERS;
  const rows = keepVitalSignsRosterRows(payload?.rows || []).map((row) => rowToVitalSignsCells(row));
  return {
    title: payload?.title || "Vital Signs Chart",
    headers,
    rows,
    columnWidthsPx: BASE_COL_WIDTHS_PX,
    name: "Vital Signs",
    // DM matches Customer Info Yes/No select.
    dataVerificationByColumn: {
      2: {
        type: "dropdown",
        type2: false,
        value1: "Yes,No",
        value2: "",
        checked: false,
        remote: false,
        prohibitInput: true,
        hintShow: false,
        hintText: "",
      },
    },
  };
}

/** Normalize sheet display Yes/No → customer field yes/no. Empty clears. */
export function diabetesValueForCustomer(displayValue) {
  const normalised = `${displayValue ?? ""}`.trim().toLowerCase();
  if (normalised === "yes") return "yes";
  if (normalised === "no") return "no";
  return "";
}

/**
 * Rows whose DM changed vs baseline and have a customer_id.
 * Returns payloads for CustomerService.updateCustomer ({ customerId, diabetes_mellitus }).
 */
export function collectDiabetesCustomerUpdates(draftRows = [], baselineRows = []) {
  const updates = [];
  const max = Math.max(draftRows.length, baselineRows.length);
  for (let i = 0; i < max; i += 1) {
    const draft = draftRows[i] || {};
    const baseline = baselineRows[i] || {};
    const customerId = `${draft.customer_id || baseline.customer_id || ""}`.trim();
    if (!customerId) continue;

    const nextDisplay = `${draft.diabetes ?? ""}`.trim();
    const prevDisplay = `${baseline.diabetes ?? ""}`.trim();
    if (nextDisplay.toLowerCase() === prevDisplay.toLowerCase()) continue;

    updates.push({
      customerId,
      diabetes_mellitus: diabetesValueForCustomer(nextDisplay),
    });
  }
  return updates;
}

/**
 * Rows whose Weight changed vs baseline and have a customer_id.
 * Returns payloads for CustomerService.updateCustomer ({ customerId, weight }).
 */
export function collectWeightCustomerUpdates(draftRows = [], baselineRows = []) {
  const updates = [];
  const max = Math.max(draftRows.length, baselineRows.length);
  for (let i = 0; i < max; i += 1) {
    const draft = draftRows[i] || {};
    const baseline = baselineRows[i] || {};
    const customerId = `${draft.customer_id || baseline.customer_id || ""}`.trim();
    if (!customerId) continue;

    const nextWeight = `${draft.weight ?? ""}`.trim();
    const prevWeight = `${baseline.weight ?? ""}`.trim();
    if (nextWeight === prevWeight) continue;

    updates.push({
      customerId,
      weight: nextWeight,
    });
  }
  return updates;
}

export function collectVitalSignsCustomerUpdates(draftRows = [], baselineRows = []) {
  const byId = {};
  collectDiabetesCustomerUpdates(draftRows, baselineRows).forEach((item) => {
    byId[item.customerId] = { ...(byId[item.customerId] || {}), diabetes_mellitus: item.diabetes_mellitus };
  });
  collectWeightCustomerUpdates(draftRows, baselineRows).forEach((item) => {
    byId[item.customerId] = { ...(byId[item.customerId] || {}), weight: item.weight };
  });
  return Object.entries(byId).map(([customerId, payload]) => ({ customerId, ...payload }));
}

export function buildVitalSignsFortuneSheetData(payload) {
  const model = buildVitalSignsSheetModel(payload);
  const sheets = buildMedicalFortuneSheetData(model);
  const sheet = sheets[0];
  if (!sheet) return sheets;

  // Explicit Yes/No dropdown on DM (col 2), same pattern as MOLST CPR.
  const dataVerification = { ...(sheet.dataVerification || {}) };
  const diabetesDropdown = {
    type: "dropdown",
    type2: false,
    value1: "Yes,No",
    value2: "",
    checked: false,
    remote: false,
    prohibitInput: true,
    hintShow: false,
    hintText: "",
  };
  const dataRowCount = Math.max(model.rows.length, (sheet.row || 0) - 2);
  for (let i = 0; i < dataRowCount; i += 1) {
    dataVerification[`${i + 2}_2`] = { ...diabetesDropdown };
  }
  sheet.dataVerification = dataVerification;
  sheet.status = 1;
  sheet.order = 0;
  if (sheet.config?.rowlen) {
    sheet.config.rowlen["1"] = 48;
  }
  return sheets;
}

/**
 * Parse FortuneSheet workbook rows back into persistable vital-sign rows.
 * Keeps customer_id from the baseline when the row still lines up by index.
 *
 * FortuneSheet pads the data grid and may wrap text with newlines; we normalize
 * so mount-time onChange does not look like a user edit.
 */
export function parseVitalSignsRowsFromWorkbook(workbook, baselineRows = []) {
  const sheetRows = workbook?.[0]?.data;
  if (!Array.isArray(sheetRows)) return baselineRows;

  // Prefer baseline length: sheet data is padded far past the chart row count.
  const rowCount =
    baselineRows.length > 0
      ? baselineRows.length
      : Math.max(0, sheetRows.length - 2);

  const parsed = [];
  for (let i = 0; i < rowCount; i += 1) {
    const row = sheetRows[i + 2] || [];
    const baseline = baselineRows[i] || {};
    const cells = [];
    for (let c = 0; c < COLUMN_COUNT; c += 1) {
      cells.push(normalizeVitalCellText(row[c]));
    }

    const noText = cells[0];
    parsed.push({
      customer_id: baseline.customer_id || "",
      no: Number(noText) || baseline.no || i + 1,
      name: vitalSignsCustomerDisplayName(baseline.name || ""),
      diabetes: cells[2] || "",
      weight: cells[3] || "",
      bp: cells[4] || "",
      sugar: baseline.sugar || "",
      tue: cells[5] || "",
      wed: cells[6] || "",
      thu: cells[7] || "",
      fri: cells[8] || "",
      sat: cells[9] || "",
      sun: cells[10] || "",
    });
  }
  return parsed;
}

function normalizeVitalCellText(cell) {
  if (cell == null) return "";
  const raw = typeof cell === "object" ? `${cell.m ?? cell.v ?? ""}` : `${cell}`;
  // Fortune wrap inserts \n; treat as display-only when reading back.
  return raw.replace(/\s+/g, " ").trim();
}

export function vitalSignsRowsEqual(a = [], b = []) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const left = a[i] || {};
    const right = b[i] || {};
    const keys = [
      "no",
      "name",
      "diabetes",
      "weight",
      "bp",
      "tue",
      "wed",
      "thu",
      "fri",
      "sat",
      "sun",
    ];
    for (const key of keys) {
      const lv = `${left[key] ?? ""}`.replace(/\s+/g, " ").trim();
      const rv = `${right[key] ?? ""}`.replace(/\s+/g, " ").trim();
      if (lv !== rv) return false;
    }
  }
  return true;
}

export async function downloadVitalSignsPdf(payload, filename = "Vital_Signs_Chart") {
  const model = buildVitalSignsSheetModel(payload);
  const customerRows = model.rows.filter((row) => `${row[1] || ""}`.trim());
  await downloadMedicalTablePdf({
    title: model.title,
    headers: model.headers,
    rows: customerRows,
    filename,
    pageOptions: {
      orientation: "landscape",
      marginX: 8,
      marginY: 8,
      fontSize: 13,
      headFontSize: 12,
      titleFontSize: 18,
      cellPadding: 3,
      headerHeight: 40,
      rowsPerPage: 14,
      lineColor: [51, 51, 51],
      titleOnFirstPageOnly: true,
    },
    columnStyles: {
      0: { cellWidth: 36, halign: "center" },
      1: { cellWidth: 150, halign: "left" },
      2: { cellWidth: 42, halign: "center" },
      3: { cellWidth: 58, halign: "center" },
      4: { cellWidth: 128, halign: "center" },
      5: { cellWidth: 60, halign: "center" },
      6: { cellWidth: 60, halign: "center" },
      7: { cellWidth: 60, halign: "center" },
      8: { cellWidth: 60, halign: "center" },
      9: { cellWidth: 60, halign: "center" },
      10: { cellWidth: 60, halign: "center" },
    },
  });
}

export async function downloadVitalSignsXlsx(payload, filename = "Vital_Signs_Chart") {
  const model = buildVitalSignsSheetModel(payload);
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Worldshine";
  const sheet = workbook.addWorksheet("Sheet1");
  const colCount = model.headers.length;
  const border80 = {
    top: { style: "thin", color: { argb: "FF333333" } },
    left: { style: "thin", color: { argb: "FF333333" } },
    bottom: { style: "thin", color: { argb: "FF333333" } },
    right: { style: "thin", color: { argb: "FF333333" } },
  };

  sheet.mergeCells(1, 1, 1, colCount);
  const titleCell = sheet.getCell(1, 1);
  titleCell.value = model.title;
  titleCell.font = { bold: true, size: 16, name: "Times New Roman" };
  titleCell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  for (let col = 1; col <= colCount; col += 1) {
    sheet.getCell(1, col).border = border80;
  }
  sheet.getRow(1).height = 38;

  const headerRow = sheet.getRow(2);
  model.headers.forEach((header, index) => {
    const cell = headerRow.getCell(index + 1);
    cell.value = header;
    cell.font = { bold: true, size: 12, name: "Arial" };
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    cell.border = border80;
  });

  model.rows.forEach((row, rowIndex) => {
    const excelRow = sheet.getRow(rowIndex + 3);
    row.forEach((value, colIndex) => {
      const cell = excelRow.getCell(colIndex + 1);
      cell.value = value || null;
      cell.font = { size: 12, name: "Arial" };
      cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
      cell.border = border80;
    });
  });

  model.columnWidthsPx.forEach((px, index) => {
    sheet.getColumn(index + 1).width = Math.max(6, Math.round(px / 7));
  });

  const buffer = await workbook.xlsx.writeBuffer();
  triggerBlobDownload(
    buffer,
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ensureExtension(filename, ".xlsx")
  );
}
